import {
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
} from 'react-hook-form'

import { getByPath } from './getByPath'

/**
 * オブジェクトから指定されたパスの値を返します。
 * 値は存在するものと見なします。
 *
 * @template T - オブジェクトの型。
 * @template P - パスの型。
 * @param obj - オブジェクト。
 * @param path - パス。
 * @returns オブジェクトのパスの値。
 */
export function pathAssume<T extends FieldValues, P extends FieldPath<T>>(
  obj: T | null | undefined,
  path: P,
): NonNullable<FieldPathValue<T, P>>

/**
 * オブジェクトから指定されたパスの値を返します。
 * 値は存在するものと見なします。
 *
 * @template T - オブジェクトの型。
 * @template P - パスの型。
 * @param path - パス。
 * @returns オブジェクトを引数に取り、オブジェクトのパスの値を返す関数。
 */
export function pathAssume<T extends FieldValues, P extends FieldPath<T>>(
  path: P,
): (obj: T | null | undefined) => NonNullable<FieldPathValue<T, P>>

/**
 * @param args - 引数。
 * @returns 結果または部分適用された関数。
 */
export function pathAssume<T extends FieldValues, P extends FieldPath<T>>(
  ...args: [P] | [T | null | undefined, P]
):
  | NonNullable<FieldPathValue<T, P>>
  | ((obj: T | null | undefined) => NonNullable<FieldPathValue<T, P>>) {
  const pathAssumeExistInner = (
    obj: T | null | undefined,
    path: P,
  ): NonNullable<FieldPathValue<T, P>> =>
    getByPath(obj, path) as NonNullable<FieldPathValue<T, P>>

  if (args.length === 1) {
    return (obj: T | null | undefined) => pathAssumeExistInner(obj, ...args)
  }

  return pathAssumeExistInner(...args)
}
